import { Suspense, useEffect } from 'react';
import { Navigate, Outlet, Route, BrowserRouter as Router, Routes, useLocation } from 'react-router';
import {
  clearPostLoginPath,
  getLoginUrl,
  getPathFromLoginSearch,
  peekPostLoginPath,
} from '@/core/auth/post-login-redirect.core';
import {
  canAccessConsumerPage,
  canAccessDeveloperPage,
  canAccessMaintainerPage,
  hasValidRole,
} from '@/core/permissions/permission.core';
import { usePermissions } from '@/hooks/use-permissions/use-permissions.hook';
import { useExplicitLogoutStore } from '@/store/explicit-logout.store';
import { LoginPage } from '@/pages/login/login.page';
import {
  MAINTAINER_HOME_ALL_ROUTE,
  MAINTAINER_HOME_DO_DETAILS_ROUTE,
  MAINTAINER_HOME_PUBLISH_DO_ROUTE,
} from '@/pages/maintainer/maintainer.core';
// import { Loader } from '@/components/ui/loader/loader.component';
import * as LazyPages from './lazy-pages';
import { PreTailoringPage } from './lazy-pages';

// Remembers the requested page so the login page can send the user back there after authentication.
// Skipped after an explicit logout, so the next user starts on their default page.
const LoginRedirect = () => {
  const { pathname, search, hash } = useLocation();
  const explicitLogout = useExplicitLogoutStore((state) => state.explicitLogout);
  const loginUrl = explicitLogout ? '/login' : getLoginUrl(`${pathname}${search}${hash}`);
  return <Navigate to={loginUrl} replace />;
};

const BadRoleRoute = () => {
  const { roles, isAuthenticated } = usePermissions();
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  if (!hasValidRole(roles)) {
    return <Outlet />;
  }
  return <Navigate to="/login" replace />;
};

const UnauthenticatedRoute = () => {
  const { roles, isAuthenticated } = usePermissions();
  const { pathname, search } = useLocation();
  // The stored path comes back from Entra. The query param covers logged in users opening a shared login link.
  // Read in render but cleared in an effect, because StrictMode renders twice.
  const sharedLinkPath = pathname === '/login' ? getPathFromLoginSearch(search) : null;
  const postLoginPath = isAuthenticated ? (peekPostLoginPath() ?? sharedLinkPath) : null;

  useEffect(() => {
    if (isAuthenticated) {
      clearPostLoginPath();
    }
  }, [isAuthenticated]);

  if (!isAuthenticated) {
    return <Outlet />;
  }
  // The target route checks roles itself and falls back to /login, which then picks the default page
  if (postLoginPath) {
    return <Navigate to={postLoginPath} replace />;
  }
  if (canAccessConsumerPage(roles)) {
    return <Navigate to="/products" replace />;
  }
  if (canAccessMaintainerPage(roles)) {
    return <Navigate to="/maintainer" replace />;
  }
  if (!hasValidRole(roles)) {
    return <Navigate to="/bad_role" replace />;
  }
  return <Outlet />;
};

const ConsumerRoute = () => {
  const { roles, isAuthenticated } = usePermissions();
  if (!isAuthenticated) {
    return <LoginRedirect />;
  }
  if (canAccessConsumerPage(roles)) {
    return <Outlet />;
  }
  return <Navigate to="/login" replace />;
};

const DeveloperRoute = () => {
  const { roles, isAuthenticated } = usePermissions();
  if (!isAuthenticated) {
    return <LoginRedirect />;
  }
  if (
    (import.meta.env.VITE_ENV === 'dev' || import.meta.env.VITE_ENV === 'qa') &&
    canAccessDeveloperPage(roles, import.meta.env.VITE_ENV)
  ) {
    return <Outlet />;
  }
  return <Navigate to="/login" replace />;
};

const MaintainerRoute = () => {
  const { roles, isAuthenticated } = usePermissions();
  if (!isAuthenticated) {
    return <LoginRedirect />;
  }
  if (canAccessMaintainerPage(roles)) {
    return <Outlet />;
  }
  return <Navigate to="/login" replace />;
};

const ConsumerRoutes = () => <ConsumerRoute />;
const MaintainerRoutes = () => <MaintainerRoute />;
const DeveloperRoutes = () => <DeveloperRoute />;
const AuthRoutes = () => <UnauthenticatedRoute />;

// Loading fallback for Suspense boundary
// NOTE: Before lazy loading all pages loaded immediately so the loader is never displayed.
// The Loader component is commented out to maintain the same behavior as before.
// Uncomment <Loader /> if you want to show a spinner during lazy loading.
const LoadingFallback = () => <div className="flex items-center justify-center min-h-screen">{/* <Loader /> */}</div>;

export const RouterContainer = () => {
  return (
    <Router>
      <Suspense fallback={<LoadingFallback />}>
        <Routes>
          <Route element={<ConsumerRoutes />}>
            <Route path="/products" element={<LazyPages.ProductGroupsPage />} />
            <Route path="/products/:groupId" element={<LazyPages.ProductsPage />} />
            <Route path="/product/:id/pre-tailoring" element={<LazyPages.PreTailoringPage />} />
            <Route path="/product/:id/pre-tailoring" element={<PreTailoringPage />} />
            <Route path="/product/:id/final-tailoring" element={<LazyPages.TailoringPage />} />
            <Route path="/product/:id/summary" element={<LazyPages.SummaryPage />} />
            <Route path="/product/:id/export" element={<LazyPages.ExportPage />} />
            <Route path="/product/:id/data" element={<LazyPages.DataPage />} />
            <Route path="/templates" element={<LazyPages.TemplatesPage />} />
            <Route path="/template/:id/pre-tailoring" element={<LazyPages.TemplatesPretailoringPage />} />
            <Route path="/template/:id/final-tailoring" element={<LazyPages.TemplatesTailoringPage />} />
            <Route path="/update-product/:itemId" element={<LazyPages.UpdateSingleItemPage />} />
            <Route path="/update-template/:itemId" element={<LazyPages.UpdateSingleItemPage />} />
            <Route path="/update-all/:pmvId" element={<LazyPages.UpdateAllPage />} />
            <Route path="/substitutions" element={<LazyPages.ManageSubstitutionsPage />} />
          </Route>
          <Route element={<MaintainerRoutes />}>
            <Route path="/maintainer" element={<Navigate to={MAINTAINER_HOME_ALL_ROUTE} replace />} />
            <Route path="/maintainer/bulk-upload" element={<LazyPages.BulkUploadPage />} />
            <Route path="/maintainer/home" element={<Navigate to={MAINTAINER_HOME_ALL_ROUTE} replace />} />
            <Route path={MAINTAINER_HOME_ALL_ROUTE} element={<LazyPages.MaintainerHomePage />} />
            <Route path={MAINTAINER_HOME_DO_DETAILS_ROUTE} element={<LazyPages.MaintainerViewEditNodePage />} />
            <Route path="/maintainer/home/:node/:id" element={<LazyPages.MaintainerViewEditNodePage />} />
            <Route path={MAINTAINER_HOME_PUBLISH_DO_ROUTE} element={<LazyPages.MaintainerPublishNodePage />} />
            <Route path="/maintainer/home/publish/do/:id" element={<LazyPages.MaintainerPublishNodePage />} />
          </Route>
          <Route element={<DeveloperRoutes />}>
            <Route path="/dev/test" element={<LazyPages.DevPage />} />
          </Route>
          <Route element={<AuthRoutes />}>
            <Route path="/redirect" element={<LazyPages.RedirectPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="*" element={<Navigate to="/login" replace />} />
          </Route>
          <Route element={<BadRoleRoute />}>
            <Route path="/bad_role" element={<LazyPages.InvalidRolePage />} />
          </Route>
        </Routes>
      </Suspense>
    </Router>
  );
};
